import { cva, type VariantProps } from "class-variance-authority";
import {
  type FieldsetHTMLAttributes,
  type ReactNode,
  forwardRef,
  useId,
} from "react";
import { cn } from "../cn";

export interface RadioGroupOption {
  readonly value: string;
  readonly label: ReactNode;
  readonly disabled?: boolean;
}

export const radioGroupVariants = cva("min-w-0 gap-0.5", {
  variants: {
    orientation: {
      vertical: "grid",
      horizontal: "flex flex-wrap gap-x-6",
      responsive:
        "flex flex-col gap-x-6 @min-[17rem]:flex-row @min-[17rem]:flex-nowrap",
    },
  },
  defaultVariants: { orientation: "vertical" },
});

export interface RadioGroupProps
  extends Omit<
      FieldsetHTMLAttributes<HTMLFieldSetElement>,
      "onChange" | "aria-required"
    >,
    VariantProps<typeof radioGroupVariants> {
  readonly legend: ReactNode;
  readonly name?: string;
  readonly required?: boolean;
  readonly value?: string;
  readonly options: readonly RadioGroupOption[];
  readonly onValueChange: (value: string) => void;
}

/** Native radios own focus and arrow keys;
 * an undefined value leaves every answer unselected. */
export const RadioGroup = forwardRef<HTMLFieldSetElement, RadioGroupProps>(
  (
    {
      legend,
      name,
      value,
      options,
      onValueChange,
      orientation,
      className,
      disabled,
      "aria-invalid": invalid,
      "aria-describedby": describedBy,
      required,
      ...props
    },
    ref,
  ) => {
    const groupId = useId();
    return (
      <fieldset
        {...props}
        ref={ref}
        disabled={disabled}
        aria-invalid={invalid}
        aria-describedby={describedBy}
        className={cn("@container min-w-0 disabled:opacity-50", className)}
      >
        <legend className="mb-1 text-sm font-medium">{legend}</legend>
        <div className={radioGroupVariants({ orientation })}>
          {options.map((option, index) => (
            <label
              key={option.value}
              className={cn(
                "flex min-h-11 w-fit max-w-full items-center gap-2.5 rounded-lg px-1 text-sm font-normal",
                disabled || option.disabled
                  ? "cursor-not-allowed"
                  : "cursor-pointer hover:bg-muted/40",
                option.disabled && "opacity-50",
              )}
            >
              <span className="relative inline-grid size-4 shrink-0 place-items-center">
                <input
                  type="radio"
                  id={`${groupId}-${index}`}
                  name={name ?? groupId}
                  value={option.value}
                  checked={value === option.value}
                  disabled={disabled || option.disabled}
                  aria-invalid={invalid}
                  aria-describedby={describedBy}
                  required={required}
                  onChange={() => onValueChange(option.value)}
                  className="peer size-4 shrink-0 appearance-none rounded-full border border-input bg-surface shadow-xs transition-colors checked:border-primary enabled:hover:border-foreground/30 aria-[invalid=true]:border-destructive forced-colors:appearance-auto"
                />
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute size-2 rounded-full bg-primary opacity-0 transition-opacity peer-checked:opacity-100 forced-colors:hidden"
                />
              </span>
              <span className="min-w-0">{option.label}</span>
            </label>
          ))}
        </div>
      </fieldset>
    );
  },
);
RadioGroup.displayName = "RadioGroup";
